import { Link } from 'react-router-dom';
import { useOwnerBillingState } from '@/hooks/queries/useOwnerBilling';
import { cn } from '@/lib/utils';
import {
  STRIP_COPY,
  formatBillingDate,
  resolveStripState,
  type TrialStripState,
} from './billingCopy';
import type { OwnerBillingStateResult } from '@/types/owner';

/**
 * The owner-billing trial strip (spec §4.2) — fills `OwnerLayout`'s `ownerBanner`
 * seam directly below the ink masthead. It self-fetches `useOwnerBillingState()` and
 * renders NOTHING (`return null`) for everything except its FOUR attention states:
 * trialing (comfortable / urgent), past-due grace, and lapsed. It never nags a paying
 * (`active`) owner, a non-claimant (`none`), a config-off env (`501`), a transient
 * error (`503`), or while loading (no layout shift).
 *
 * DESIGN (Ledger, spec §4.2 + §7): a full-width LIGHT band with a hairline bottom
 * rule — a paper/gold/amber/danger wash, NEVER the ink instrument panel (the "one
 * dark element per page" budget is reserved for the masthead + the billing-page
 * hero). Small-caps kicker · plain-language line · a `ledger-figure` mono `daysLeft`
 * · one gold CTA to `/owner/billing`.
 *
 * REGRESSION LOCK R3 (spec §6.2): every rendered kicker / CTA / figure is derived
 * ONLY from the mocked payload's `status` / `isEntitled` / `daysLeft` / `trialEndsAt`
 * — no hard-coded date or number, no fabricated urgency.
 */

interface BandStyle {
  band: string;
  rule: string;
  kicker: string;
  cta: string;
}

/** Ledger band treatments per state — light washes only (never ink). */
const BAND_STYLE: Record<TrialStripState, BandStyle> = {
  'trialing-comfortable': {
    band: 'bg-[color:var(--l-gold-wash)]',
    rule: 'border-[color:var(--l-gold-line)]',
    kicker: 'text-[color:var(--l-gold-deep)]',
    cta: 'border-[color:var(--l-gold-deep)] bg-[color:var(--l-gold)] text-[color:var(--l-ink)] hover:bg-[color:var(--l-gold-deep)] hover:text-[color:var(--l-paper-raised)]',
  },
  'trialing-urgent': {
    band: 'bg-[color:rgba(154,106,18,0.08)]',
    rule: 'border-[color:rgba(154,106,18,0.35)]',
    kicker: 'text-[color:var(--l-amber)]',
    cta: 'border-[color:var(--l-gold-deep)] bg-[color:var(--l-gold)] text-[color:var(--l-ink)] hover:bg-[color:var(--l-gold-deep)] hover:text-[color:var(--l-paper-raised)]',
  },
  'trialing-final': {
    band: 'bg-[color:rgba(154,106,18,0.08)]',
    rule: 'border-[color:rgba(154,106,18,0.35)]',
    kicker: 'text-[color:var(--l-amber)]',
    // Checkout is blocked in the final-48h window (spec §4.4): a disabled affordance,
    // not a gold CTA. Style below is applied to a real <button disabled>, not a Link.
    cta: 'border-[color:var(--l-rule)] bg-[color:var(--l-paper-raised)] text-[color:var(--l-gray)] cursor-not-allowed opacity-70',
  },
  grace: {
    band: 'bg-[color:rgba(155,59,50,0.06)]',
    rule: 'border-[color:rgba(155,59,50,0.3)]',
    kicker: 'text-[color:var(--l-danger)]',
    cta: 'border-[color:var(--l-gold-deep)] bg-[color:var(--l-gold)] text-[color:var(--l-ink)] hover:bg-[color:var(--l-gold-deep)] hover:text-[color:var(--l-paper-raised)]',
  },
  lapsed: {
    band: 'bg-[color:rgba(155,59,50,0.06)]',
    rule: 'border-[color:rgba(155,59,50,0.3)]',
    kicker: 'text-[color:var(--l-danger)]',
    cta: 'border-[color:var(--l-gold-deep)] bg-[color:var(--l-gold)] text-[color:var(--l-ink)] hover:bg-[color:var(--l-gold-deep)] hover:text-[color:var(--l-paper-raised)]',
  },
};

export function OwnerBillingStrip() {
  const { data } = useOwnerBillingState();
  const stripState = resolveStripState(data);
  // Nothing to show — active / none / 501 / 503 / loading, or a mapped-null state.
  if (stripState === null || data === undefined) return null;

  const style = BAND_STYLE[stripState];
  const { kicker, body, cta } = stripCopy(stripState, data);
  // Final-48h: checkout is genuinely blocked, so the CTA is a DISABLED button with the
  // honest label — never a live-looking Link that silently goes nowhere (spec §1.5).
  const ctaBlocked = stripState === 'trialing-final';
  const ctaClass = cn(
    'inline-flex shrink-0 items-center rounded-[var(--l-r-sm)] border px-3.5 py-1.5 text-[0.78rem] font-semibold transition-colors duration-[var(--l-dur-fast)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--l-gold)] focus-visible:ring-offset-2',
    style.cta,
  );

  return (
    <div
      className={cn(
        'raises-surface w-full border-b px-4 py-2.5 sm:px-6',
        style.band,
        style.rule,
      )}
      role="status"
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2">
        <span className={cn('ledger-sc shrink-0', style.kicker)}>{kicker}</span>
        <p className="min-w-0 flex-1 text-[0.82rem] leading-snug text-[color:var(--l-ink-70)]">
          {body}
        </p>
        {ctaBlocked ? (
          <button type="button" disabled className={ctaClass} aria-disabled="true">
            {cta}
          </button>
        ) : (
          <Link to="/owner/billing" className={ctaClass}>
            {cta}
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Derive the exact kicker / body / CTA for a strip state from the payload. The
 * `{n}` / `{date}` come from `daysLeft` / `trialEndsAt` only (R3 — never fabricated).
 */
function stripCopy(
  stripState: TrialStripState,
  data: OwnerBillingStateResult,
): { kicker: string; body: string; cta: string } {
  const trialEndsAt = data.status === 'none' ? '' : formatBillingDate(data.trialEndsAt);
  const daysLeft = data.daysLeft;
  switch (stripState) {
    case 'trialing-comfortable':
      return {
        kicker: STRIP_COPY.trialingComfortable.kicker,
        body: STRIP_COPY.trialingComfortable.body(daysLeft),
        cta: STRIP_COPY.trialingComfortable.cta,
      };
    case 'trialing-urgent':
      return {
        kicker: STRIP_COPY.trialingUrgent.kicker,
        body: STRIP_COPY.trialingUrgent.body(trialEndsAt, daysLeft),
        cta: STRIP_COPY.trialingUrgent.cta,
      };
    case 'trialing-final':
      return {
        kicker: STRIP_COPY.finalHours.kicker,
        body: STRIP_COPY.finalHours.body(trialEndsAt),
        cta: STRIP_COPY.finalHours.cta,
      };
    case 'grace':
      return {
        kicker: STRIP_COPY.grace.kicker,
        body: STRIP_COPY.grace.body,
        cta: STRIP_COPY.grace.cta,
      };
    case 'lapsed':
      return {
        kicker: STRIP_COPY.lapsed.kicker,
        body: STRIP_COPY.lapsed.body,
        cta: STRIP_COPY.lapsed.cta,
      };
  }
}

export default OwnerBillingStrip;
